// Helper de layout de PDF compartido (Módulo H, Grupo 1) — usado por
// `formularioGenerico.ts` (los ~50 formularios del catálogo: Tramo A/B,
// discretos, 001, 024, FEMO), `femo.ts` y la copia de historia clínica de
// hce-chart. Movido de packages/hce-chart/src/lib/pdfLayout.ts a este paquete
// compartido el 2026-09-12, cuando el servidor pasó a armar los PDFs firmados:
// las fuentes ahora llegan como bytes (`FuentesPdf`), cada runtime las carga
// a su manera.
//
// Rediseño 2026-09-03 — pedido explícito del usuario: "todos estos
// formularios... deben cumplir con la misma línea de diseño que tenemos
// para la receta médica" (`formulario/recetas/pdfReceta.ts`, el único PDF
// ya rediseñado hasta ahora — tamaño carta, tipografía Newsreader/IBM Plex
// Sans embebida, paleta navy/gris-azulado OKLCH→sRGB). Antes de este
// cambio este archivo generaba PDFs A4 con Helvetica estándar, un sistema
// visual completamente distinto y sin ningún punto de reuso con la
// receta. La API pública (`crearCursorPdf`, `NAVY`/`INK`/`MUTED`,
// `wrapText`, `MARGIN`/`PAGE_W`/`PAGE_H`) se mantiene idéntica a propósito
// — así los ~50 formularios que ya llaman a esto se retematizan solos, sin
// tocar cada uno — pero sus VALORES y la implementación de `crearCursorPdf`
// cambian para usar las fuentes/colores/tamaño de página de la receta.
import { PDFDocument, rgb, type Color, type PDFFont, type PDFPage } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import type { FuentesPdf } from './fuentes';

// Mismos valores que `formulario/recetas/pdfReceta.ts` (comentario de
// cabecera ahí: colores OKLCH del handoff de diseño, convertidos a sRGB
// con `culori`/`formatHex`) — nombres de export viejos (`NAVY`/`INK`/
// `MUTED`) se mantienen para no romper a los callers existentes.
export const NAVY = rgb(0, 0x39 / 255, 0x57 / 255);
export const LINEA_HEADER = rgb(0, 0x4f / 255, 0x6a / 255);
export const INK = rgb(0x16 / 255, 0x1c / 255, 0x1e / 255);
export const GRIS_OSCURO = rgb(0x3d / 255, 0x4a / 255, 0x51 / 255);
export const MUTED = rgb(0x6c / 255, 0x73 / 255, 0x76 / 255);
export const BORDE_SUTIL = rgb(0xc7 / 255, 0xcf / 255, 0xd3 / 255);
export const FONDO_PAGINA = rgb(0xfc / 255, 0xfe / 255, 0xfe / 255);

// Carta (8.5×11in) — mismo tamaño que pdfReceta.ts, antes A4.
export const MARGIN = 54; // 0.75in
export const PAGE_W = 612;
export const PAGE_H = 792;

/**
 * Fecha/hora impresa en el PDF, SIEMPRE en hora de Ecuador: desde que el
 * servidor (UTC) arma los PDFs firmados, `toLocaleString('es-EC')` a secas
 * imprimiría la hora corrida 5 horas.
 */
export function fechaHoraEc(iso: string): string {
  return new Date(iso).toLocaleString('es-EC', { timeZone: 'America/Guayaquil' });
}

export function fechaEc(iso: string): string {
  return new Date(iso).toLocaleDateString('es-EC', { timeZone: 'America/Guayaquil' });
}

function envolverParrafo(parrafo: string, font: PDFFont, size: number, maxWidth: number): string[] {
  const lineas: string[] = [];
  let actual = '';
  for (const palabra of parrafo.split(/\s+/)) {
    const intento = actual ? `${actual} ${palabra}` : palabra;
    if (font.widthOfTextAtSize(intento, size) > maxWidth && actual) {
      lineas.push(actual);
      actual = palabra;
    } else {
      actual = intento;
    }
  }
  if (actual) lineas.push(actual);
  return lineas;
}

/**
 * Parte `text` en líneas que caben en `maxWidth`. Los saltos de línea del texto (`\n`, `\r\n`) se RESPETAN: cada uno empieza una línea nueva
 * (un informe de enfermería de varias líneas no se imprime corrido en un solo párrafo). Las líneas en blanco de en medio se conservan
 * (`''`); las de los extremos se descartan. Sin saltos de línea, el resultado es el de siempre.
 */
export function wrapText(text: string, font: PDFFont, size: number, maxWidth: number): string[] {
  const lineas = text.split(/\r?\n/).flatMap((parrafo) => {
    const l = envolverParrafo(parrafo, font, size, maxWidth);
    return l.length > 0 ? l : [''];
  });
  while (lineas.length > 0 && lineas[0] === '') lineas.shift();
  while (lineas.length > 0 && lineas[lineas.length - 1] === '') lineas.pop();
  return lineas;
}

export interface OpcionesTexto {
  readonly bold?: boolean;
  /** Newsreader-SemiBold en vez de IBM Plex Sans — mismo uso que el título "Receta médica" o el nombre del medicamento en pdfReceta.ts: la jerarquía visual más alta de la página. */
  readonly serif?: boolean;
  readonly size?: number;
  readonly color?: Color;
}

export interface CursorPdf {
  readonly doc: PDFDocument;
  /** Escribe texto (con wrap automático), avanza el cursor, salta de página si hace falta. */
  texto(texto: string, opts?: OpcionesTexto): void;
  /** Etiqueta en mayúsculas con tracking manual (letter-spacing) — mismo patrón que `drawTracked` en pdfReceta.ts para "PACIENTE"/"ALERGIAS"/etc. */
  etiqueta(texto: string, opts?: { color?: Color }): void;
  /** Mueve el cursor sin dibujar nada — mismo uso que `y -= N` en los originales. */
  espacio(px: number): void;
  /** Línea horizontal divisoria en la posición actual del cursor. */
  linea(): void;
  /**
   * Reserva `alto` px de espacio vertical (mismo salto de página automático
   * que `texto()`/`linea()`) y entrega la `PDFPage` cruda + el origen
   * (esquina superior izquierda del área reservada) + el ancho disponible,
   * para que el caller dibuje con primitivas de `pdf-lib`
   * (`drawRectangle`/`drawText`/`drawLine`/`drawSvgPath`) — escape hatch para
   * diagramas bespoke que no encajan en texto/líneas (odontograma de 033;
   * a futuro, silueta de lesiones de 008 y partograma de 051). También recibe
   * la fuente sans-regular ya embebida en el documento (para rotular el
   * diagrama sin volver a embeber una fuente).
   */
  dibujo(alto: number, fn: (pagina: PDFPage, origen: { x: number; yTop: number }, anchoDisponible: number, fontRegular: PDFFont) => void): void;
  guardar(): Promise<Uint8Array>;
}

/** Arranca un documento carta nuevo con Newsreader-SemiBold (serif) + IBM Plex Sans Regular/SemiBold embebidas, fondo de página y el cursor en la esquina superior — mismo sistema visual que pdfReceta.ts. */
export async function crearCursorPdf(fuentes: FuentesPdf): Promise<CursorPdf> {
  const doc = await PDFDocument.create();
  doc.registerFontkit(fontkit);
  const [fontSerif, fontRegular, fontBold] = await Promise.all([
    doc.embedFont(fuentes.serifSemiBold),
    doc.embedFont(fuentes.sansRegular),
    doc.embedFont(fuentes.sansSemiBold),
  ]);

  function nuevaPagina(): PDFPage {
    const p = doc.addPage([PAGE_W, PAGE_H]);
    p.drawRectangle({ x: 0, y: 0, width: PAGE_W, height: PAGE_H, color: FONDO_PAGINA });
    return p;
  }

  let page: PDFPage = nuevaPagina();
  let y = PAGE_H - MARGIN;
  const maxWidth = PAGE_W - MARGIN * 2;

  function nuevaPaginaSiHaceFalta(alto: number): void {
    if (y - alto < MARGIN) {
      page = nuevaPagina();
      y = PAGE_H - MARGIN;
    }
  }

  return {
    doc,
    texto(contenido, opts = {}) {
      const font = opts.serif ? fontSerif : opts.bold ? fontBold : fontRegular;
      const size = opts.size ?? 10;
      const color = opts.color ?? INK;
      for (const l of wrapText(contenido, font, size, maxWidth)) {
        nuevaPaginaSiHaceFalta(size + 4);
        page.drawText(l, { x: MARGIN, y, size, font, color });
        y -= size + 4;
      }
    },
    etiqueta(contenido, opts = {}) {
      const size = 9;
      nuevaPaginaSiHaceFalta(size + 4);
      const texto = contenido.toUpperCase();
      let cx = MARGIN;
      for (const ch of texto) {
        page.drawText(ch, { x: cx, y, size, font: fontRegular, color: opts.color ?? GRIS_OSCURO });
        cx += fontRegular.widthOfTextAtSize(ch, size) + 0.6;
      }
      y -= size + 4;
    },
    espacio(px) {
      y -= px;
    },
    linea() {
      nuevaPaginaSiHaceFalta(20);
      page.drawLine({ start: { x: MARGIN, y }, end: { x: PAGE_W - MARGIN, y }, thickness: 1, color: BORDE_SUTIL });
      y -= 16;
    },
    dibujo(alto, fn) {
      nuevaPaginaSiHaceFalta(alto);
      fn(page, { x: MARGIN, yTop: y }, maxWidth, fontRegular);
      y -= alto;
    },
    async guardar() {
      return doc.save();
    },
  };
}
